import { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Input,
  DatePicker,
  Button,
  Popover,
  Tag,
  message,
  Pagination,
} from "antd";
import { CloseCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { SiMicrosoftexcel } from "react-icons/si";
import { IoAlertCircleOutline } from "react-icons/io5";
import moment from "moment";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";
import { API } from "../../../config/API";
import { POST_NO_LOGOUT } from "../../../utils/apirequest";

const { RangePicker } = DatePicker;

interface LoadOpts {
  page: number;
  pageSize: number;
  search: string;
  range: string[] | null;
}

function ResponsesTable(props: any) {
  const { formId } = props;
  const fieldDefs: any[] = useMemo(
    () => (Array.isArray(props?.fields) ? props.fields : []),
    [props?.fields]
  );

  const User = useSelector((state: any) => state?.User);
  const isAdmin = User?.user?.role?.id === 4;

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState<string[] | null>(null);
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const searchTimerRef = useRef<any>(null);

  useEffect(() => {
    loadResponses({ page: 1, pageSize: 10, search: "", range: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const loadResponses = async (opts: LoadOpts) => {
    try {
      setLoading(true);
      const reqBody: any = {
        formId: Number(formId) || formId,
        page: opts.page,
        pageSize: opts.pageSize,
      };
      if (opts.search.trim()) reqBody.search = opts.search.trim();
      if (opts.range?.[0] && opts.range?.[1]) {
        reqBody.startDate = opts.range[0];
        reqBody.endDate = opts.range[1];
      }

      const res: any = await POST_NO_LOGOUT(API.DYNAMIC_FORM_RESPONSES, reqBody);
      // Backend returns { data: [...], total: N } when pagination params are sent,
      // or a plain array when the CMS hasn't been restarted yet — handle both.
      const list: any[] = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const parsed = list
        .filter((it: any) => it?.data && it.data !== "[object Object]")
        .map((it: any, idx: number) => {
          let values: any = {};
          try {
            values = JSON.parse(it.data);
          } catch {
            values = {};
          }
          return {
            ...values,
            key: it?.id ?? idx,
            id: it?.id,
            createdAt: it?.createdAt,
          };
        });
      setRows(parsed);
      setTotal(typeof res?.total === "number" ? res.total : parsed.length);
    } catch (err: any) {
      console.log("responses error", err);
      if (err?.response?.status === 401) {
        message.error(
          "Session expired — please log out and log in again to view responses."
        );
      } else {
        message.error("Could not load responses.");
      }
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Client-side sort on the current page's rows + correct serial numbers.
  const displayRows = useMemo(() => {
    const { columnKey, order } = sortedInfo || {};
    let result = rows;
    if (columnKey && order) {
      result = [...rows].sort((a, b) => {
        let cmp: number;
        if (columnKey === "createdAt") {
          cmp = moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf();
        } else {
          cmp = String(a[columnKey] ?? "").localeCompare(
            String(b[columnKey] ?? ""),
            undefined,
            { numeric: true }
          );
        }
        return order === "descend" ? -cmp : cmp;
      });
    }
    const startSerial = (page - 1) * pageSize;
    return result.map((r, i) => ({ ...r, __serial: startSerial + i + 1 }));
  }, [rows, sortedInfo, page, pageSize]);

  const columns = useMemo(() => {
    const serial = {
      title: "#",
      key: "serial",
      dataIndex: "__serial",
      width: 60,
    };
    const dynamic = fieldDefs.map((f) => ({
      title: f?.label || f?.name,
      dataIndex: f?.name,
      key: f?.name,
      sorter: true,
      sortOrder: sortedInfo.columnKey === f?.name ? sortedInfo.order : null,
      render: (val: any) =>
        val === undefined || val === null || val === "" ? "-" : String(val),
    }));
    const created = {
      title: "Submitted On",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      sortOrder:
        sortedInfo.columnKey === "createdAt" ? sortedInfo.order : null,
      render: (val: any) =>
        val ? moment(val).format("DD-MM-YYYY hh:mm A") : "-",
    };
    return [serial, ...dynamic, created];
  }, [fieldDefs, sortedInfo]);

  const onTableChange = (_pagination: any, _filters: any, sorter: any) => {
    setSortedInfo({ columnKey: sorter?.columnKey, order: sorter?.order });
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadResponses({ page: 1, pageSize, search: value, range });
    }, 300);
  };

  const handleRangeChange = (_: any, dateStrings: [string, string]) => {
    const newRange =
      dateStrings?.[0] && dateStrings?.[1]
        ? (dateStrings as string[])
        : null;
    setRange(newRange);
    setPage(1);
    loadResponses({ page: 1, pageSize, search, range: newRange });
  };

  // Export: calls the same endpoint without pagination params so the backend
  // returns all rows matching the active filters (not just the current page).
  const exportExcel = async () => {
    try {
      setExporting(true);
      const reqBody: any = { formId: Number(formId) || formId };
      if (search.trim()) reqBody.search = search.trim();
      if (range?.[0] && range?.[1]) {
        reqBody.startDate = range[0];
        reqBody.endDate = range[1];
      }

      const res: any = await POST_NO_LOGOUT(API.DYNAMIC_FORM_RESPONSES, reqBody);
      const list: any[] = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const exportRows = list
        .filter((it: any) => it?.data && it.data !== "[object Object]")
        .map((it: any, idx: number) => {
          let values: any = {};
          try {
            values = JSON.parse(it.data);
          } catch {
            values = {};
          }
          return {
            ...values,
            key: it?.id ?? idx,
            id: it?.id,
            createdAt: it?.createdAt,
          };
        })
        .map((r: any, i: number) => ({ ...r, __serial: i + 1 }));

      const headers = [
        "#",
        ...fieldDefs.map((f) => f?.label || f?.name),
        "Submitted On",
      ];
      const excelBody = exportRows.map((r: any) => [
        r.__serial,
        ...fieldDefs.map((f) => r[f?.name] ?? ""),
        r.createdAt ? moment(r.createdAt).format("DD-MM-YYYY hh:mm A") : "",
      ]);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...excelBody]);
      XLSX.utils.book_append_sheet(wb, ws, "Responses");
      XLSX.writeFile(wb, "form-responses.xlsx");
      setExporting(false);
    } catch (err) {
      console.log(err);
      message.error("Export failed.");
      setExporting(false);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <h5 style={{ margin: 0 }}>
          Responses{" "}
          <Tag color="blue" style={{ marginLeft: 4 }}>
            {total}
          </Tag>
        </h5>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Input
            allowClear
            placeholder="Search responses"
            style={{ width: 220 }}
            value={search}
            onChange={(e: any) => handleSearchChange(e.target.value)}
          />
          <RangePicker
            allowClear
            format="YYYY-MM-DD"
            onChange={handleRangeChange as any}
          />
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => loadResponses({ page, pageSize, search, range })}
          >
            Reload
          </Button>
          {isAdmin && (
            <Popover
              title="Export Report"
              content={
                <div style={{ whiteSpace: "pre-line", width: 320 }}>
                  <IoAlertCircleOutline size={15} /> Exports all rows matching the
                  current search and date filter.
                </div>
              }
            >
              <Button
                type="primary"
                icon={<SiMicrosoftexcel />}
                loading={exporting}
                disabled={total === 0}
                onClick={exportExcel}
              >
                Export
              </Button>
            </Popover>
          )}
        </div>
      </div>

      {(search || (range && range[0])) && (
        <div style={{ marginTop: 8 }}>
          {search && (
            <Tag style={{ marginBottom: 5 }}>
              search: {search}{" "}
              <CloseCircleOutlined onClick={() => handleSearchChange("")} />
            </Tag>
          )}
          {range && range[0] && (
            <Tag style={{ marginBottom: 5 }}>
              {range[0]} → {range[1]}{" "}
              <CloseCircleOutlined
                onClick={() => {
                  setRange(null);
                  setPage(1);
                  loadResponses({ page: 1, pageSize, search, range: null });
                }}
              />
            </Tag>
          )}
        </div>
      )}

      <Table
        style={{ marginTop: 12 }}
        size="small"
        loading={loading}
        columns={columns}
        dataSource={displayRows}
        onChange={onTableChange}
        scroll={{ x: true }}
        pagination={false}
        className="commen-table-custom-scrollbar"
      />
      <br />
      <div style={{ float: "right" }}>
        <Pagination
          responsive
          current={page}
          total={total}
          pageSize={pageSize}
          pageSizeOptions={["10", "20", "50", "100"]}
          showSizeChanger
          onChange={(p, ps) => {
            setPage(p);
            setPageSize(ps);
            loadResponses({ page: p, pageSize: ps, search, range });
          }}
        />
      </div>
    </div>
  );
}

export default ResponsesTable;
